import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  InputAdornment,
  Alert,
  Snackbar,
  Grid,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TablePagination,
  CircularProgress,
  Skeleton,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  Plus,
  Search as SearchIcon,
  Eye,
  Printer,
  Trash2,
  RefreshCw,
  Package,
} from 'lucide-react';
import {
  getBills,
  addBill,
  updateBill,
  deleteBill,
  migrateBillsWithCategory,
} from '../firebase/billingService';
import { getInventoryItems, updateStock } from '../firebase/inventoryService';
import { getCustomers, updateCustomerStats, addCustomer } from '../firebase/customerService';
import { createOrUpdateCreditor, removeBillFromCreditor } from '../firebase/creditorService';
import { logActivity, ACTIONS, ENTITY_TYPES } from '../firebase/activityLogService';
import { getBillingSettings } from '../firebase/settingsService';
import { deductStockFIFO, restoreStockFIFO } from '../firebase/stockBatchService';
import { printProfessionalInvoice } from '../utils/professionalInvoice';
import { checkLoyaltyEligibility, getLoyaltyDiscountSettings, calculateLoyaltyDiscount, getCustomerLoyaltyTier } from '../firebase/loyaltyService';
import { creditCashAccount, debitCashAccount } from '../firebase/cashBankService';
import PaymentSplitDialog from './PaymentSplitDialog';
import PaymentSplitBadge from './PaymentSplitBadge';
import {
  createLedgerEntriesForSplit,
} from '../firebase/paymentSplitService';
import { collection, query, where, getDocs, getDoc, updateDoc, serverTimestamp, doc } from 'firebase/firestore';
import { db } from '../firebase/config';

const Billing = ({ currentUser, permissions }) => {
  const isAdmin = permissions?.userType === 'admin';
  const canDelete = isAdmin || permissions?.deleteBilling === true;

  // Helper: get today's date in local time as YYYY-MM-DD (avoids UTC timezone shift)
  const localDateStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [inventorySearchTerm, setInventorySearchTerm] = useState('');
  const [bills, setBills] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [formData, setFormData] = useState({ customer: '', date: localDateStr(), status: 'Pending', paymentType: 'Cash', customerType: 'Retail', note: '' });
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [selectedItems, setSelectedItems] = useState([]);
  const selectedItemsRef = useRef(null); // Ref for auto-scroll
  const [discount, setDiscount] = useState({ type: 'percentage', value: 0 });
  const [billingSettings, setBillingSettings] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showQuickAddCustomer, setShowQuickAddCustomer] = useState(false);
  const [quickCustomerData, setQuickCustomerData] = useState({ name: '', phone: '', email: '' });
  
  // Loyalty discount states
  const [loyaltyDiscount, setLoyaltyDiscount] = useState({
    eligible: false,
    applied: false,
    amount: 0,
    customerData: null,
    settings: null,
    tier: null
  });
  const [checkingLoyalty, setCheckingLoyalty] = useState(false);

  // Decant size selector
  const [decantDialog, setDecantDialog] = useState({ open: false, item: null, size: 5 });
  
  // Loading states for action buttons
  const [actionLoading, setActionLoading] = useState({});
  const [submitting, setSubmitting] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Payment split state
  const [paymentSplitDialog, setPaymentSplitDialog] = useState(false);
  const [paymentSplit, setPaymentSplit] = useState({
    cash: 0,
    bank: 0,
    koko: 0,
    mintpay: 0,
  });

  // Check loyalty eligibility when customer is selected
  const checkCustomerLoyalty = useCallback(async (customerName) => {
    if (!customerName || typeof customerName !== 'string' || customerName.trim() === '') {
      setLoyaltyDiscount({
        eligible: false,
        applied: false,
        amount: 0,
        customerData: null,
        settings: null,
        tier: null
      });
      return;
    }

    setCheckingLoyalty(true);

    try {
      // Get loyalty settings
      const settingsResult = await getLoyaltyDiscountSettings();
      
      if (!settingsResult.success || !settingsResult.enabled) {
        setLoyaltyDiscount({
          eligible: false,
          applied: false,
          amount: 0,
          customerData: null,
          settings: settingsResult,
          tier: null
        });
        setCheckingLoyalty(false);
        return;
      }

      // Check if customer is eligible
      const eligibilityResult = await checkLoyaltyEligibility(customerName);
      
      if (eligibilityResult.success && eligibilityResult.isLoyal) {
        const tier = await getCustomerLoyaltyTier(eligibilityResult.customerData.totalOrders);
        
        setLoyaltyDiscount({
          eligible: true,
          applied: false, // Will be applied when user clicks "Apply"
          amount: 0,
          customerData: eligibilityResult.customerData,
          settings: settingsResult,
          tier: tier
        });
        
        // Show notification
        showSnackbar(
          `${eligibilityResult.customerData.name} is a ${tier.tier} customer! Loyalty discount available.`,
          'success'
        );
      } else {
        setLoyaltyDiscount({
          eligible: false,
          applied: false,
          amount: 0,
          customerData: eligibilityResult.customerData || null,
          settings: settingsResult,
          tier: null
        });
      }
    } catch (error) {
      // silently fail — loyalty check is non-critical
    } finally {
      setCheckingLoyalty(false);
    }
  }, []);

  // ── Calculation helpers (defined before loyalty callbacks that use them) ──
  const calculateSubtotal = useCallback(() => {
    return selectedItems.reduce((sum, item) => sum + (item.totalPrice || (item.price * item.quantity)), 0);
  }, [selectedItems]);

  const calculateDiscount = () => {
    const subtotal = calculateSubtotal();
    let discountAmt = 0;
    if (discount.type === 'percentage') {
      discountAmt = (subtotal * parseFloat(discount.value || 0)) / 100;
    } else {
      discountAmt = parseFloat(discount.value || 0);
    }
    return Math.max(0, Math.min(discountAmt, subtotal));
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscount();
    const loyaltyAmt = loyaltyDiscount.applied ? (loyaltyDiscount.amount || 0) : 0;
    const afterDiscount = subtotal - discountAmount - loyaltyAmt;
    const delivery = formData.paymentType === 'COD' ? (parseFloat(deliveryCharge) || 0) : 0;
    if (formData.paymentType === 'Koko') {
      const kokoChargePercent = billingSettings?.kokoChargePercentage || 12;
      const kokoCharge = (afterDiscount * kokoChargePercent) / 100;
      return afterDiscount + kokoCharge;
    }
    if (formData.paymentType === 'MintPay') {
      const mintPayChargePercent = billingSettings?.mintPayChargePercentage || 12;
      const mintPayCharge = (afterDiscount * mintPayChargePercent) / 100;
      return afterDiscount + mintPayCharge;
    }
    return afterDiscount + delivery;
  };

  const calculateKokoCharge = () => {
    if (formData.paymentType !== 'Koko') return 0;
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscount();
    const loyaltyAmt = loyaltyDiscount.applied ? (loyaltyDiscount.amount || 0) : 0;
    const afterDiscount = subtotal - discountAmount - loyaltyAmt;
    const kokoChargePercent = billingSettings?.kokoChargePercentage || 12;
    return (afterDiscount * kokoChargePercent) / 100;
  };

  const calculateMintPayCharge = () => {
    if (formData.paymentType !== 'MintPay') return 0;
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscount();
    const loyaltyAmt = loyaltyDiscount.applied ? (loyaltyDiscount.amount || 0) : 0;
    const afterDiscount = subtotal - discountAmount - loyaltyAmt;
    const mintPayChargePercent = billingSettings?.mintPayChargePercentage || 12;
    return (afterDiscount * mintPayChargePercent) / 100;
  };

  // Apply loyalty discount
  const applyLoyaltyDiscount = useCallback(() => {
    if (!loyaltyDiscount.eligible || !loyaltyDiscount.settings) return;

    const subtotal = calculateSubtotal();
    // Guard against undefined/NaN — fall back to 0 if not configured
    const baseDiscountValue = parseFloat(loyaltyDiscount.settings.discountValue) || 0;
    const bonusDiscount = loyaltyDiscount.tier?.bonusDiscount || 0;
    // If both are 0 (settings not configured), use 5% as safe default
    const totalDiscountValue = (baseDiscountValue + bonusDiscount) || 5;
    const discountType = loyaltyDiscount.settings.discountType || 'percentage';

    const discountAmount = calculateLoyaltyDiscount(
      subtotal,
      discountType,
      totalDiscountValue
    );

    // Store loyalty discount separately — do NOT set main discount state
    // to avoid double-counting on the invoice
    setLoyaltyDiscount(prev => ({
      ...prev,
      applied: true,
      amount: discountAmount,
      discountType,
      discountValue: totalDiscountValue,
    }));

    showSnackbar(
      `Loyalty discount applied! ${loyaltyDiscount.tier.tier} tier: ${totalDiscountValue}${discountType === 'percentage' ? '%' : ' LKR'} off`,
      'success'
    );
  }, [loyaltyDiscount, calculateSubtotal]);

  // Remove loyalty discount
  const removeLoyaltyDiscount = useCallback(() => {
    setDiscount({ type: 'percentage', value: 0 });
    setLoyaltyDiscount(prev => ({
      ...prev,
      applied: false,
      amount: 0
    }));
    showSnackbar('Loyalty discount removed', 'info');
  }, []);

  // Load data once on mount
  const loadBills = useCallback(async () => {
    setLoading(true);
    const result = await getBills();
    if (result.success) {
      setBills(prevBills => {
        // Intelligent merge - protect critical billing fields
        const existingMap = new Map(prevBills.map(b => [b.id, b]));
        
        return result.data.map(newBill => {
          const existing = existingMap.get(newBill.id);
          
          if (!existing) {
            return newBill; // New bill, use as-is
          }
          
          // PROTECT IMMUTABLE FINANCIAL FIELDS
          return {
            ...newBill,
            // Critical fields that should NEVER change
            invoiceNumber: existing.invoiceNumber,
            customer: existing.customer,
            customerId: existing.customerId,
            total: existing.total,
            subtotal: existing.subtotal,
            createdAt: existing.createdAt,
            // Protect items array from being emptied
            items: (newBill.items && newBill.items.length > 0) 
              ? newBill.items 
              : existing.items || [],
          };
        });
      });
    } else {
      showSnackbar(result.error || 'Failed to load bills', 'error');
    }
    setLoading(false);
  }, []);

  const loadInventory = useCallback(async () => {
    const result = await getInventoryItems();
    if (result.success) {
      setInventory(result.data);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    const result = await getCustomers();
    if (result.success) {
      setCustomers(result.data);
    }
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      await loadBills();
      await loadInventory();
      await loadCustomers();

      // Load billing settings for global discount
      const settingsResult = await getBillingSettings();
      if (settingsResult.success) setBillingSettings(settingsResult.data);
      
      // Migrate old bills to add category from inventory
      const inventoryResult = await getInventoryItems();
      if (inventoryResult.success) {
        await migrateBillsWithCategory(inventoryResult.data);
        // Reload bills after migration
        await loadBills();
      }
    };
    
    initializeData();
  }, [loadBills, loadInventory, loadCustomers]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadBills(), loadInventory(), loadCustomers()]);
    setRefreshing(false);
    showSnackbar('Data refreshed');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Quick Add Customer
  const handleQuickAddCustomer = async () => {
    if (!quickCustomerData.name.trim()) {
      showSnackbar('Please enter customer name', 'error');
      return;
    }

    const customerData = {
      name: quickCustomerData.name,
      phone: quickCustomerData.phone || '',
      email: quickCustomerData.email || '',
      status: 'Active',
      address: '',
      totalOrders: 0,
      totalSpent: 0,
      createdAt: new Date().toISOString(),
    };

    const result = await addCustomer(customerData);
    if (result.success) {
      setCustomers(prevCustomers => [...prevCustomers, { id: result.id, ...customerData }]);
      setFormData({ ...formData, customer: quickCustomerData.name });
      setShowQuickAddCustomer(false);
      setQuickCustomerData({ name: '', phone: '', email: '' });
      showSnackbar('Customer added successfully');
    } else {
      showSnackbar(result.error || 'Failed to add customer', 'error');
    }
  };

  // Print bill
  const handlePrint = async (bill) => {
    try {
      const settingsResult = await getBillingSettings();
      const settings = settingsResult.success ? settingsResult.data : (billingSettings || {});
      await printProfessionalInvoice(bill, settings);
      showSnackbar('Opening print dialog...');
    } catch (error) {
      showSnackbar('Failed to open print dialog', 'error');
    }
  };

  // Download bill as PDF
  const filteredBills = bills.filter(bill => {
    const customer = (bill.customer || bill.customerName || '').toLowerCase();
    const id = (bill.id || '').toLowerCase();
    const invoiceNumber = (bill.invoiceNumber || '').toLowerCase();
    const term = searchTerm.toLowerCase();
    return customer.includes(term) || id.includes(term) || invoiceNumber.includes(term);
  });

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Paginate bills
  const paginatedBills = filteredBills.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleCreate = async () => {
    if (submitting) return; // Prevent double-click
    
    if (!formData.customer.trim() || selectedItems.length === 0) {
      showSnackbar('Please select customer and at least one item', 'error');
      return;
    }

    setSubmitting(true);

    try {
      // EDIT flow — diff items to determine stock changes, only touch what changed
      if (editingBill) {
        const oldItems = editingBill.items || [];
        const newItems = selectedItems;

        // Check if items/quantities actually changed
        const itemsChanged = (() => {
          if (oldItems.length !== newItems.length) return true;
          return newItems.some(newItem => {
            const oldItem = oldItems.find(o => o.id === newItem.id);
            return !oldItem || oldItem.quantity !== newItem.quantity;
          });
        })();

        let updatedBatchesUsed = editingBill.batchesUsed || [];
        let updatedCOGS = editingBill.costOfGoodsSold || 0;

        if (itemsChanged) {
          // For each item, calculate the quantity difference and adjust stock
          const allItemIds = new Set([
            ...oldItems.map(i => i.id),
            ...newItems.map(i => i.id)
          ]);

          for (const itemId of allItemIds) {
            const oldItem = oldItems.find(i => i.id === itemId);
            const newItem = newItems.find(i => i.id === itemId);
            const oldQty = oldItem?.quantity || 0;
            const newQty = newItem?.quantity || 0;
            const diff = newQty - oldQty;

            if (diff === 0) continue;

            if (diff > 0) {
              // Quantity increased or new item added — deduct more stock via FIFO
              const inventoryItem = inventory.find(inv => inv.id === itemId);
              if (!inventoryItem || inventoryItem.stock < diff) {
                showSnackbar(`Insufficient stock for ${newItem?.name}`, 'error');
                setSubmitting(false);
                return;
              }
              const fifoResult = await deductStockFIFO(itemId, diff);
              if (!fifoResult.success) throw new Error(`Stock deduction failed for ${newItem?.name}`);

              // Merge new batchesUsed into existing
              const existing = updatedBatchesUsed.find(b => b.itemId === itemId);
              if (existing) {
                // Merge batches
                for (const newBatch of fifoResult.batchesUsed) {
                  const existingBatch = existing.batchesUsed.find(b => b.batchId === newBatch.batchId);
                  if (existingBatch) {
                    existingBatch.quantityUsed += newBatch.quantityUsed;
                    existingBatch.totalCost += newBatch.totalCost;
                  } else {
                    existing.batchesUsed.push(newBatch);
                  }
                }
                existing.quantity = newQty;
              } else {
                updatedBatchesUsed.push({
                  itemId,
                  itemName: newItem.name,
                  quantity: newQty,
                  batchesUsed: fifoResult.batchesUsed
                });
              }
              updatedCOGS += fifoResult.batchesUsed.reduce((s, b) => s + b.totalCost, 0);

            } else {
              // Quantity decreased or item removed — restore stock to batches
              const restoreQty = Math.abs(diff);
              const itemBatchData = updatedBatchesUsed.find(b => b.itemId === itemId);

              if (itemBatchData?.batchesUsed?.length > 0) {
                // Proportionally scale down batches oldest-first
                const batchesToRestore = [];
                let remaining = restoreQty;
                for (const batch of itemBatchData.batchesUsed) {
                  if (remaining <= 0) break;
                  const restoreFromBatch = Math.min(batch.quantityUsed, remaining);
                  batchesToRestore.push({ ...batch, quantityUsed: restoreFromBatch });
                  remaining -= restoreFromBatch;
                }
                await restoreStockFIFO(itemId, batchesToRestore);

                if (newQty === 0) {
                  // Item fully removed
                  updatedBatchesUsed = updatedBatchesUsed.filter(b => b.itemId !== itemId);
                } else {
                  // Update remaining batch quantities
                  let toRemove = restoreQty;
                  for (const batch of itemBatchData.batchesUsed) {
                    if (toRemove <= 0) break;
                    const removed = Math.min(batch.quantityUsed, toRemove);
                    batch.quantityUsed -= removed;
                    batch.totalCost = batch.costPrice * batch.quantityUsed;
                    toRemove -= removed;
                  }
                  itemBatchData.batchesUsed = itemBatchData.batchesUsed.filter(b => b.quantityUsed > 0);
                  itemBatchData.quantity = newQty;
                }
                updatedCOGS = updatedBatchesUsed.reduce((s, b) =>
                  s + b.batchesUsed.reduce((bs, batch) => bs + batch.totalCost, 0), 0);
              } else {
                // No batch data — fallback flat restore
                const inventoryItem = inventory.find(inv => inv.id === itemId);
                if (inventoryItem) await updateStock(itemId, inventoryItem.stock + restoreQty);
              }
            }
          }
        }

        const newSubtotal = newItems.reduce((s, i) => s + (i.totalPrice || i.price * i.quantity), 0);
        const newDiscountAmt = discount.type === 'percentage'
          ? (newSubtotal * parseFloat(discount.value || 0)) / 100
          : parseFloat(discount.value || 0);
        const newKokoCharge = formData.paymentType === 'Koko'
          ? ((newSubtotal - newDiscountAmt) * (billingSettings?.kokoChargePercentage || 12)) / 100
          : 0;
        const newMintPayCharge = formData.paymentType === 'MintPay'
          ? ((newSubtotal - newDiscountAmt) * (billingSettings?.mintPayChargePercentage || 12)) / 100
          : 0;
        const newTotal = newSubtotal - newDiscountAmt + newKokoCharge + newMintPayCharge;
        const newStatus = (formData.paymentType === 'Cash' || formData.paymentType === 'Card')
          ? 'Paid'
          : formData.paymentType === 'Koko'
          ? 'Koko'
          : formData.paymentType === 'MintPay'
          ? 'MintPay'
          : 'Pending';

        const patch = {
          note: formData.note || '',
          customer: formData.customer,
          date: formData.date || editingBill.date,
          paymentType: formData.paymentType || 'Cash',
          customerType: formData.customerType || 'Retail',
          status: newStatus,
          discount: { type: discount.type, value: discount.value, amount: newDiscountAmt },
          ...(Object.values(paymentSplit).filter(v => v > 0).length > 1 && {
            paymentSplit,
          }),
          ...(formData.paymentType === 'Koko' && {
            kokoCharge: {
              percentage: billingSettings?.kokoChargePercentage || 12,
              amount: newKokoCharge,
            },
          }),
          ...(formData.paymentType === 'MintPay' && {
            mintPayCharge: {
              percentage: billingSettings?.mintPayChargePercentage || 12,
              amount: newMintPayCharge,
            },
          }),
          ...(loyaltyDiscount.applied && {
            loyaltyDiscount: {
              applied: true,
              tier: loyaltyDiscount.tier?.tier || '',
              amount: loyaltyDiscount.amount,
              discountType: loyaltyDiscount.settings?.discountType || 'percentage',
              discountValue: loyaltyDiscount.settings?.discountValue || 0,
            },
          }),
          ...(itemsChanged && {
            items: newItems.map(i => ({
              id: i.id, name: i.name, category: i.category,
              itemCode: i.itemCode || '',
              quantity: i.quantity, price: i.price,
              totalPrice: i.totalPrice || (i.price * i.quantity),
              ...(i.hasBatchDiscounts && { hasBatchDiscounts: true, batchBreakdown: i.batchBreakdown || [] }),
            })),
            subtotal: newSubtotal,
            total: newTotal,
            batchesUsed: updatedBatchesUsed,
            costOfGoodsSold: updatedCOGS,
            grossProfit: newTotal - updatedCOGS,
          }),
          ...(!itemsChanged && { total: newTotal, subtotal: newSubtotal }),
        };

        const result = await updateBill(editingBill.id, patch);
        if (result.success) {
          setBills(prevBills => prevBills.map(b => b.id === editingBill.id ? { ...b, ...patch } : b));
          await loadInventory();

          const oldPaymentType = editingBill.paymentType || 'Cash';
          const newPaymentType = patch.paymentType;
          const isCreditType = (t) => t === 'Credit' || t === 'COD' || t === 'Koko' || t === 'MintPay';
          const isCashType = (t) => t === 'Cash' || t === 'Card';
          const wasCredit = isCreditType(oldPaymentType);
          const isNowCredit = isCreditType(newPaymentType);
          const wasCash = isCashType(oldPaymentType);
          const isNowCash = isCashType(newPaymentType);

          try {
            if (wasCredit && !isNowCredit) {
              // Changed FROM credit TO cash/card — remove from creditor using OLD total, credit cash with NEW total
              if (editingBill.creditorId) {
                await removeBillFromCreditor(editingBill.creditorId, editingBill.id, editingBill.total || 0);
                await updateBill(editingBill.id, { creditorId: null });
              }
              // Credit cash with new total (what customer actually pays now)
              const accountType = newPaymentType === 'Card' ? 'cash_at_bank' : 'petty_cash';
              creditCashAccount(accountType, newTotal, editingBill.id, 'sale',
                `Sale - ${editingBill.invoiceNumber || editingBill.id} - ${formData.customer}`,
                newPaymentType.toLowerCase(), null, currentUser
              ).catch(err => console.error('Cash ledger credit failed:', err));
            } else if (!wasCredit && isNowCredit) {
              // Changed FROM cash/card TO credit — reverse the cash entry, create creditor
              if (wasCash) {
                const oldAccountType = oldPaymentType === 'Card' ? 'cash_at_bank' : 'petty_cash';
                debitCashAccount(oldAccountType, editingBill.total || 0, editingBill.id, 'sale_reversal',
                  `Sale Reversal (type change) - ${editingBill.invoiceNumber || editingBill.id} - ${formData.customer}`,
                  oldPaymentType.toLowerCase(), null, currentUser
                ).catch(err => console.error('Cash ledger reversal failed:', err));
              }
              // Create creditor entry
              let customer = customers.find(c => c.name === formData.customer);
              if (!customer) {
                const addResult = await addCustomer({ name: formData.customer, phone: '', email: '', type: formData.customerType || 'Retail' });
                if (addResult.success) customer = { id: addResult.id, name: formData.customer };
              }
              if (customer) {
                const invoiceDate = formData.date || editingBill.date;
                let dueDate = null;
                if (newPaymentType === 'Koko' || newPaymentType === 'MintPay') {
                  const d = new Date(invoiceDate);
                  d.setDate(d.getDate() + 15);
                  dueDate = d.toISOString().split('T')[0];
                }
                const creditorResult = await createOrUpdateCreditor(customer.id, {
                  id: editingBill.id,
                  customerName: customer.name,
                  customerPhone: customer.phone || '',
                  customerEmail: customer.email || '',
                  customer: customer.name,
                  total: newTotal,
                  paymentType: newPaymentType,
                  dueDate,
                  invoiceDate,
                });
                if (creditorResult.success) {
                  await updateBill(editingBill.id, { creditorId: creditorResult.creditorId });
                }
              }
            } else if (wasCredit && isNowCredit && editingBill.creditorId) {
              // Still credit — sync total if it changed
              const oldTotal = editingBill.total || 0;
              const totalDiff = newTotal - oldTotal;
              if (Math.abs(totalDiff) > 0.01) {
                const creditorRef = doc(db, 'creditors', editingBill.creditorId);
                const creditorSnap = await getDoc(creditorRef);
                if (creditorSnap.exists()) {
                  const cd = creditorSnap.data();
                  const updatedCredited = (cd.totalCredited || 0) + totalDiff;
                  const updatedBalance = updatedCredited - (cd.totalPaid || 0);
                  await updateDoc(creditorRef, {
                    totalCredited: updatedCredited,
                    balance: Math.max(0, updatedBalance),
                    status: updatedBalance > 0 ? 'Active' : 'Settled',
                    updatedAt: serverTimestamp(),
                  });
                }
              }
            } else if (wasCash && isNowCash) {
              // Still cash/card — handle account type change AND/OR total change
              const oldAccountType = oldPaymentType === 'Card' ? 'cash_at_bank' : 'petty_cash';
              const newAccountType = newPaymentType === 'Card' ? 'cash_at_bank' : 'petty_cash';
              const oldTotal = editingBill.total || 0;
              const totalDiff = newTotal - oldTotal;
              const accountChanged = oldAccountType !== newAccountType;

              if (accountChanged) {
                // Reverse old account entry and create new one
                debitCashAccount(oldAccountType, oldTotal, editingBill.id, 'sale_reversal',
                  `Sale Reversal (type change) - ${editingBill.invoiceNumber || editingBill.id}`,
                  oldPaymentType.toLowerCase(), null, currentUser
                ).catch(() => {});
                creditCashAccount(newAccountType, newTotal, editingBill.id, 'sale',
                  `Sale - ${editingBill.invoiceNumber || editingBill.id} - ${formData.customer}`,
                  newPaymentType.toLowerCase(), null, currentUser
                ).catch(() => {});
              } else if (Math.abs(totalDiff) > 0.01) {
                // Same account, total changed — adjust the difference
                if (totalDiff > 0) {
                  creditCashAccount(newAccountType, totalDiff, editingBill.id, 'sale_adjustment',
                    `Sale Adjustment - ${editingBill.invoiceNumber || editingBill.id}`,
                    newPaymentType.toLowerCase(), null, currentUser
                  ).catch(() => {});
                } else {
                  debitCashAccount(newAccountType, Math.abs(totalDiff), editingBill.id, 'sale_adjustment',
                    `Sale Adjustment - ${editingBill.invoiceNumber || editingBill.id}`,
                    newPaymentType.toLowerCase(), null, currentUser
                  ).catch(() => {});
                }
              }
            }
          } catch (e) {
            console.error('Creditor/ledger sync on bill update failed:', e);
          }

          // Sync customer stats if customer changed or total changed
          try {
            const oldCustomerName = editingBill.customer;
            const newCustomerName = formData.customer;
            const oldTotal = editingBill.total || 0;
            const customerChanged = oldCustomerName !== newCustomerName;

            if (customerChanged) {
              // Customer changed - update both old and new customer stats
              const oldCustomer = customers.find(c => c.name === oldCustomerName);
              const newCustomer = customers.find(c => c.name === newCustomerName);

              // Decrease old customer stats
              if (oldCustomer) {
                const updatedOrders = Math.max(0, (oldCustomer.totalOrders || 0) - 1);
                const updatedSpent = Math.max(0, (oldCustomer.totalSpent || 0) - oldTotal);
                await updateCustomerStats(oldCustomer.id, updatedOrders, updatedSpent);
                setCustomers(prevCustomers => prevCustomers.map(c =>
                  c.id === oldCustomer.id
                    ? { ...c, totalOrders: updatedOrders, totalSpent: updatedSpent }
                    : c
                ));
              }

              // Increase new customer stats
              if (newCustomer) {
                const updatedOrders = (newCustomer.totalOrders || 0) + 1;
                const updatedSpent = (newCustomer.totalSpent || 0) + newTotal;
                await updateCustomerStats(newCustomer.id, updatedOrders, updatedSpent);
                setCustomers(prevCustomers => prevCustomers.map(c =>
                  c.id === newCustomer.id
                    ? { ...c, totalOrders: updatedOrders, totalSpent: updatedSpent }
                    : c
                ));
              }
            } else if (Math.abs(newTotal - oldTotal) > 0.01) {
              // Same customer but total changed - adjust spent amount
              const customer = customers.find(c => c.name === newCustomerName);
              if (customer) {
                const totalDiff = newTotal - oldTotal;
                const updatedSpent = Math.max(0, (customer.totalSpent || 0) + totalDiff);
                await updateCustomerStats(customer.id, customer.totalOrders || 0, updatedSpent);
                setCustomers(prevCustomers => prevCustomers.map(c =>
                  c.id === customer.id
                    ? { ...c, totalSpent: updatedSpent }
                    : c
                ));
              }
            }
          } catch (e) {
            console.error('Customer stats sync on bill update failed:', e);
          }

          closeModal();
          showSnackbar('Invoice updated successfully');
          await logActivity({
            userId: currentUser?.uid,
            userEmail: currentUser?.email,
            userName: currentUser?.displayName || currentUser?.email,
            action: ACTIONS.UPDATED_BILL,
            entityType: ENTITY_TYPES.BILL,
            entityId: editingBill.id,
            entityName: `Invoice for ${formData.customer}`,
            details: { customer: formData.customer, total: newTotal, itemsChanged }
          });
        } else {
          showSnackbar(result.error || 'Failed to update invoice', 'error');
        }
        return;
      }

      // CREATE flow — check stock and deduct via FIFO
      // For decant items, group by inventoryId and check total ML needed
      const decantMLNeeded = {};
      for (const item of selectedItems) {
        if (item.isDecant) {
          const invId = item.inventoryId || item.id;
          decantMLNeeded[invId] = (decantMLNeeded[invId] || 0) + (item.quantity * item.decantML);
        }
      }
      const decantStockOk = Object.entries(decantMLNeeded).every(([invId, mlNeeded]) => {
        const inv = inventory.find(i => i.id === invId);
        return inv && inv.stock >= mlNeeded;
      });
      const regularStockOk = selectedItems.filter(i => !i.isDecant).every(selectedItem => {
        const inventoryItem = inventory.find(inv => inv.id === selectedItem.id);
        return inventoryItem && inventoryItem.stock >= selectedItem.quantity;
      });
      const stockCheck = decantStockOk && regularStockOk;

      if (!stockCheck) {
        showSnackbar('Insufficient stock for one or more items', 'error');
        setSubmitting(false);
        return;
      }
      let stockUpdateResults = [];
      let totalCOGS = 0;

      // Group decant items by inventoryId to avoid race condition on same ML pool
      const decantDeductMap = {}; // inventoryId -> total ML to deduct
      const regularSelectedItems = [];

      for (const selectedItem of selectedItems) {
        if (selectedItem.isDecant) {
          const invId = selectedItem.inventoryId || selectedItem.id;
          const mlToDeduct = selectedItem.quantity * selectedItem.decantML;
          decantDeductMap[invId] = (decantDeductMap[invId] || 0) + mlToDeduct;
        } else {
          regularSelectedItems.push(selectedItem);
        }
      }

      // Deduct decant ML — one sequential write per inventory item
      const { updateStock: updateStockFn } = await import('../firebase/inventoryService');
      for (const [invId, mlToDeduct] of Object.entries(decantDeductMap)) {
        const inventoryItem = inventory.find(inv => inv.id === invId);
        if (inventoryItem) {
          await updateStockFn(invId, Math.max(0, inventoryItem.stock - mlToDeduct));
          stockUpdateResults.push({
            itemId: invId,
            itemName: inventoryItem.name,
            quantity: mlToDeduct,
            batchesUsed: [],
            totalCOGS: (inventoryItem.costPrice || 0) * mlToDeduct,
          });
          totalCOGS += (inventoryItem.costPrice || 0) * mlToDeduct;
        }
      }

      // Update inventory stock using FIFO for regular items
      const stockUpdatePromises = regularSelectedItems.map(async (selectedItem) => {
        const inventoryItem = inventory.find(inv => inv.id === selectedItem.id);
        if (inventoryItem) {
          // Use FIFO deduction
          const fifoResult = await deductStockFIFO(selectedItem.id, selectedItem.quantity);
          
          if (!fifoResult.success) {
            throw new Error(`Failed to deduct stock for ${inventoryItem.name}: ${fifoResult.error}`);
          }
          
          // Track which batches were used for COGS calculation
          return {
            itemId: selectedItem.id,
            itemName: inventoryItem.name,
            quantity: selectedItem.quantity,
            batchesUsed: fifoResult.batchesUsed,
            totalCOGS: fifoResult.batchesUsed.reduce((sum, b) => sum + b.totalCost, 0)
          };
        }
      });

      stockUpdateResults = await Promise.all(stockUpdatePromises);
      
      // Add regular items COGS to totalCOGS (decant COGS already added above)
      totalCOGS += stockUpdateResults.reduce((sum, result) => sum + (result?.totalCOGS || 0), 0);

      const billData = {
        customer: formData.customer,
        items: selectedItems.map(item => ({ 
          id: item.id,
          name: item.name,
          category: item.category,
          quantity: item.quantity, 
          price: item.price,
          totalPrice: item.totalPrice || (item.price * item.quantity),
          ...(item.hasBatchDiscounts && {
            hasBatchDiscounts: true,
            batchBreakdown: item.batchBreakdown || [],
          }),
          ...(item.isDecant && {
            isDecant: true,
            decantML: item.decantML,
            inventoryId: item.inventoryId,
          }),
        })),
        subtotal: calculateSubtotal(),
        discount: {
          type: discount.type,
          value: discount.value,
          amount: calculateDiscount()
        },
        // Add payment split if enabled
        ...(Object.values(paymentSplit).filter(v => v > 0).length > 1 && {
          paymentSplit,
        }),
        // Add Koko charge information if payment type is Koko
        ...(formData.paymentType === 'Koko' && {
          kokoCharge: {
            percentage: billingSettings?.kokoChargePercentage || 12,
            amount: calculateKokoCharge()
          }
        }),
        // Add MintPay charge information if payment type is MintPay
        ...(formData.paymentType === 'MintPay' && {
          mintPayCharge: {
            percentage: billingSettings?.mintPayChargePercentage || 12,
            amount: calculateMintPayCharge()
          }
        }),
        // Add delivery charge for COD
        deliveryCharge: formData.paymentType === 'COD' ? (parseFloat(deliveryCharge) || 0) : 0,
        total: calculateTotal(),
        date: formData.date || localDateStr(),
        // Set status based on payment type
        // Cash/Card = Paid immediately (money received at time of sale)
        // Credit/COD/Koko = Pending (money not yet received)
        status: (formData.paymentType === 'Cash' || formData.paymentType === 'Card') ? 'Paid' : (formData.paymentType === 'Koko' ? 'Koko' : (formData.paymentType === 'MintPay' ? 'MintPay' : 'Pending')),
        paymentType: formData.paymentType || 'Cash',
        customerType: formData.customerType || 'Retail',
        // FIFO & Tiered Pricing Data
        batchesUsed: stockUpdateResults.map(r => ({
          itemId: r.itemId,
          itemName: r.itemName,
          quantity: r.quantity,
          batchesUsed: r.batchesUsed
        })),
        costOfGoodsSold: totalCOGS,
        grossProfit: calculateTotal() - totalCOGS,
        note: formData.note || '',
        ...(loyaltyDiscount.applied && {
          loyaltyDiscount: {
            applied: true,
            tier: loyaltyDiscount.tier?.tier || '',
            amount: loyaltyDiscount.amount,
            discountType: loyaltyDiscount.settings?.discountType || 'percentage',
            discountValue: loyaltyDiscount.settings?.discountValue || 0,
          },
        }),
      };

      // Create the bill first
      const result = await addBill(billData);
      if (result.success) {
        // If payment type is Credit, COD, or Koko, create/update creditor
        if (formData.paymentType === 'Credit' || formData.paymentType === 'COD' || formData.paymentType === 'Koko' || formData.paymentType === 'MintPay') {
          let customer = customers.find(c => c.name === formData.customer);
          
          // If customer doesn't exist, create them first
          if (!customer) {
            const newCustomerData = {
              name: formData.customer,
              phone: '',
              email: '',
              status: 'Active',
              totalOrders: 0,
              totalSpent: 0
            };
            
            const customerResult = await addCustomer(newCustomerData);
            if (customerResult.success) {
              customer = { id: customerResult.id, ...newCustomerData };
              setCustomers(prevCustomers => [...prevCustomers, customer]);
            } else {
              showSnackbar('Warning: Failed to create customer for creditor', 'warning');
            }
          } else {
            // customer found, proceed
          }
          
          if (customer) {
            
            // Calculate due date for Koko payments (15 days from invoice date)
            const invoiceDate = formData.date || localDateStr();
            let dueDate = null;
            if (formData.paymentType === 'Koko' || formData.paymentType === 'MintPay') {
              const dueDateObj = new Date(invoiceDate);
              dueDateObj.setDate(dueDateObj.getDate() + 15);
              dueDate = dueDateObj.toISOString().split('T')[0];
            }
            
            const creditorResult = await createOrUpdateCreditor(customer.id, {
              id: result.id,
              customerName: customer.name,
              customerPhone: customer.phone || '',
              customerEmail: customer.email || '',
              customer: customer.name,
              total: calculateTotal(),
              paymentType: formData.paymentType,
              dueDate: dueDate,
              invoiceDate: invoiceDate
            });
            
            if (creditorResult.success) {
              await updateBill(result.id, { ...billData, creditorId: creditorResult.creditorId });
            } else {
              showSnackbar('Warning: Failed to create creditor record', 'warning');
            }
          } else {
            showSnackbar('Warning: Could not create creditor - customer not found', 'warning');
          }
        }

        // Update local inventory state
        setInventory(prevInventory => prevInventory.map(item => {
          const selectedItem = selectedItems.find(si => si.id === item.id);
          if (selectedItem) {
            return { ...item, stock: item.stock - selectedItem.quantity };
          }
          return item;
        }));

        // Update customer stats
        const customer = customers.find(c => c.name === formData.customer);
        if (customer) {
          const newTotalOrders = (customer.totalOrders || 0) + 1;
          const newTotalSpent = (customer.totalSpent || 0) + calculateTotal();
          await updateCustomerStats(customer.id, newTotalOrders, newTotalSpent);
          setCustomers(prevCustomers => prevCustomers.map(c => 
            c.id === customer.id 
              ? { ...c, totalOrders: newTotalOrders, totalSpent: newTotalSpent }
              : c
          ));
        }

        // Cash & Bank: Handle payment split or single payment method
        const hasPaymentSplit = Object.values(paymentSplit).filter(v => v > 0).length > 1;
        
        if (hasPaymentSplit) {
          // Payment split enabled - create ledger entries for each method
          const ledgerEntries = createLedgerEntriesForSplit(
            paymentSplit,
            result.id,
            result.invoiceNumber || result.id,
            formData.customer,
            billingSettings
          );

          // Create ledger entries for each payment method
          for (const entry of ledgerEntries) {
            creditCashAccount(
              entry.accountType,
              entry.amount,
              entry.referenceId,
              entry.referenceType,
              entry.description,
              entry.paymentMethod,
              null,
              currentUser
            ).catch(err => console.error('Cash ledger entry failed:', err));
          }

          // Store payment split in bill record
          await updateBill(result.id, { paymentSplit });
        } else if (formData.paymentType === 'Cash' || formData.paymentType === 'Card') {
          // Single payment method (Cash or Card)
          const accountType = formData.paymentType === 'Card' ? 'cash_at_bank' : 'petty_cash';
          creditCashAccount(
            accountType,
            calculateTotal(),
            result.id,
            'sale',
            `Sale - ${result.invoiceNumber || result.id} - ${formData.customer}`,
            formData.paymentType.toLowerCase(),
            null,
            currentUser
          ); // fire-and-forget — non-critical
        }

        setBills(prevBills => [{ 
          id: result.id, 
          invoiceNumber: result.invoiceNumber, // Add the invoice number
          ...billData, 
          createdAt: new Date().toISOString() 
        }, ...prevBills]);
        closeModal();
        const successMessage = (formData.paymentType === 'Credit' || formData.paymentType === 'COD' || formData.paymentType === 'Koko' || formData.paymentType === 'MintPay')
          ? 'Invoice created and creditor updated successfully'
          : 'Invoice created, inventory and customer stats updated successfully';
        showSnackbar(successMessage);
        
        // Log activity
        await logActivity({
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          action: ACTIONS.CREATED_BILL,
          entityType: ENTITY_TYPES.BILL,
          entityId: result.id,
          entityName: `Invoice ${result.invoiceNumber || result.id} for ${formData.customer}`,
          details: {
            invoiceNumber: result.invoiceNumber,
            customer: formData.customer,
            total: calculateTotal(),
            paymentType: formData.paymentType,
            customerType: formData.customerType,
            itemCount: selectedItems.length
          }
        });
      } else {
        showSnackbar(result.error || 'Failed to create invoice', 'error');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      showSnackbar('Failed to create invoice', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (bill) => {
    // Prevent editing bills with returns
    if (bill.returnStatus) {
      showSnackbar('Cannot edit bills with returns. Please delete the return records first from the Returns page.', 'error');
      return;
    }
    
    setEditingBill(bill);
    setFormData({
      customer: bill.customer,
      date: bill.date,
      status: bill.status,
      paymentType: bill.paymentType || 'Cash',
      customerType: bill.customerType || 'Retail',
      note: bill.note || '',
    });
    // Restore delivery charge if COD bill
    setDeliveryCharge(bill.paymentType === 'COD' ? (bill.deliveryCharge || 0) : 0);
    setSelectedItems(bill.items.map(item => ({
      id: item.id || item.name,
      name: item.name,
      category: item.category,
      price: item.price,
      quantity: item.quantity,
      totalPrice: item.totalPrice || (item.price * item.quantity),
      hasBatchDiscounts: item.hasBatchDiscounts || false,
      batchBreakdown: item.batchBreakdown || [],
    })));
    setDiscount(bill.discount || { type: 'percentage', value: 0 });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (actionLoading[`delete-${id}`]) return; // Prevent double-click
    
    setActionLoading(prev => ({ ...prev, [`delete-${id}`]: true }));
      
      try {
        const billToDelete = bills.find(b => b.id === id);
        
        // Prevent deletion of returned bills
        if (billToDelete && billToDelete.returnStatus) {
          showSnackbar('Cannot delete returned bills. Please delete the return records first from the Returns page.', 'error');
          setActionLoading(prev => ({ ...prev, [`delete-${id}`]: false }));
          return;
        }
        
        if (billToDelete && billToDelete.items) {
          
          // Group decant items by inventoryId to avoid race condition
          const decantRestoreMap = {};
          const regularBillItems = [];

          for (const item of billToDelete.items) {
            if (item.isDecant && item.inventoryId && item.decantML) {
              const mlToRestore = item.quantity * item.decantML;
              decantRestoreMap[item.inventoryId] = (decantRestoreMap[item.inventoryId] || 0) + mlToRestore;
            } else {
              regularBillItems.push(item);
            }
          }

          // Restore decant ML — one sequential write per inventory item
          for (const [inventoryId, mlToRestore] of Object.entries(decantRestoreMap)) {
            const decantInvItem = inventory.find(inv => inv.id === inventoryId);
            if (decantInvItem) {
              await updateStock(inventoryId, (decantInvItem.stock || 0) + mlToRestore);
            }
          }

          // Restore regular items via FIFO in parallel
          const restorePromises = regularBillItems.map(async (item) => {
            const inventoryItem = inventory.find(inv => inv.id === item.id);
            
            if (inventoryItem) {
              if (billToDelete.batchesUsed) {
                const itemBatchData = billToDelete.batchesUsed.find(b => b.itemId === item.id);
                if (itemBatchData && itemBatchData.batchesUsed && itemBatchData.batchesUsed.length > 0) {
                  return restoreStockFIFO(item.id, itemBatchData.batchesUsed);
                }
              }
              // Fallback for bills without batch data
              const newStock = inventoryItem.stock + item.quantity;
              return updateStock(item.id, newStock);
            } else {
              return { success: true, skipped: true };
            }
          });
          
          const restoreResults = await Promise.all(restorePromises);
          
          // Check if any restoration failed
          const failedRestores = restoreResults.filter(r => !r.success && !r.skipped);
          if (failedRestores.length > 0) {
            console.error('Some stock restorations failed:', failedRestores);
            showSnackbar('Warning: Some items could not be restored to inventory', 'warning');
          }
          
          // Reload inventory to get updated batch data
          await loadInventory();
        }
        
        // Update customer stats (decrement)
        if (billToDelete && billToDelete.customer) {
          const customer = customers.find(c => c.name === billToDelete.customer);
          if (customer) {
            const newTotalOrders = Math.max(0, (customer.totalOrders || 0) - 1);
            const newTotalSpent = Math.max(0, (customer.totalSpent || 0) - (billToDelete.total || 0));
            await updateCustomerStats(customer.id, newTotalOrders, newTotalSpent);
            
            // Update local customer state
            setCustomers(prevCustomers => prevCustomers.map(c => 
              c.id === customer.id 
                ? { ...c, totalOrders: newTotalOrders, totalSpent: newTotalSpent }
                : c
            ));
          }
        }
        
        // Cash & Bank: reverse entry for Cash/Card bill deletions
        if (billToDelete && (billToDelete.paymentType === 'Cash' || billToDelete.paymentType === 'Card')) {
          const accountType = billToDelete.paymentType === 'Card' ? 'cash_at_bank' : 'petty_cash';
          debitCashAccount(
            accountType,
            billToDelete.total || 0,
            id,
            'sale_reversal',
            `Sale Reversal - ${billToDelete.invoiceNumber || id} - ${billToDelete.customer}`,
            billToDelete.paymentType.toLowerCase(),
            null,
            currentUser
          ); // fire-and-forget
        }

        // If this was a credit, COD, or Koko bill, remove from creditor
        if (billToDelete && (billToDelete.paymentType === 'Credit' || billToDelete.paymentType === 'COD' || billToDelete.paymentType === 'Koko' || billToDelete.paymentType === 'MintPay')) {
          if (billToDelete.creditorId) {
            await removeBillFromCreditor(
              billToDelete.creditorId, 
              id, 
              billToDelete.total
            );
          } else {
            // Old bill without creditorId - find creditor by customer name
            const customer = customers.find(c => c.name === billToDelete.customer);
            if (customer) {
              // Find creditor by customerId
              const creditorsSnapshot = await getDocs(
                query(collection(db, 'creditors'), where('customerId', '==', customer.id))
              );
              if (!creditorsSnapshot.empty) {
                const creditorDoc = creditorsSnapshot.docs[0];
                await removeBillFromCreditor(
                  creditorDoc.id,
                  id,
                  billToDelete.total
                );
            }
          }
        }
      }
      
      // Delete the bill
      const result = await deleteBill(id);
      
      if (result.success) {
        setBills(prevBills => prevBills.filter(b => b.id !== id));
        showSnackbar('Invoice deleted and stock restored to correct batches');
        
        // Log activity
        await logActivity({
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          userName: currentUser?.displayName || currentUser?.email,
          action: ACTIONS.DELETED_BILL,
          entityType: ENTITY_TYPES.BILL,
          entityId: id,
          entityName: `Invoice for ${billToDelete?.customer || 'Unknown'}`,
          details: {
            customer: billToDelete?.customer,
            total: billToDelete?.total,
            itemsRestored: billToDelete?.items?.length || 0
          }
        });
      } else {
        showSnackbar(result.error || 'Failed to delete invoice', 'error');
        await loadBills();
      }
      } catch (error) {
        console.error('Error deleting invoice:', error);
        showSnackbar('Failed to delete invoice', 'error');
      } finally {
        setActionLoading(prev => ({ ...prev, [`delete-${id}`]: false }));
      }
  };

  // Calculate FIFO pricing for accurate multi-batch pricing
  const calculateFIFOPricing = (inventoryItem, quantity, customerType) => {
    if (!inventoryItem.batches || inventoryItem.batches.length === 0) {
      // No batches, use inventory price
      const price = customerType === 'Wholesale' 
        ? (inventoryItem.wholesalePrice || inventoryItem.sellingPrice || inventoryItem.retailPrice)
        : (inventoryItem.retailPrice || inventoryItem.sellingPrice);
      return {
        totalPrice: price * quantity,
        averagePrice: price,
        batchBreakdown: [],
        singleBatch: true
      };
    }

    // Check if global discount is enabled
    const globalDiscountEnabled = billingSettings?.globalDiscount?.enabled || false;

    // Sort batches by createdAt (FIFO)
    const sortedBatches = [...inventoryItem.batches].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    let remainingQty = quantity;
    let totalCost = 0;
    const breakdown = [];

    for (const batch of sortedBatches) {
      if (remainingQty <= 0) break;
      
      const qtyFromBatch = Math.min(batch.quantityRemaining, remainingQty);
      
      // Get base price based on customer type
      let basePrice = customerType === 'Wholesale'
        ? (batch.wholesalePrice || 0)
        : (batch.retailPrice || 0);
      
      // Apply batch discount if global discount is disabled and batch has discount
      const batchDiscount = batch.discount || 0;
      const shouldApplyBatchDiscount = !globalDiscountEnabled && batchDiscount > 0;
      
      let finalPrice = basePrice;
      if (shouldApplyBatchDiscount) {
        // Apply discount: discountedPrice = originalPrice * (1 - discount/100)
        finalPrice = basePrice * (1 - batchDiscount / 100);
      }
      
      totalCost += qtyFromBatch * finalPrice;
      breakdown.push({
        batchId: batch.batchId,
        quantity: qtyFromBatch,
        originalPrice: basePrice,
        discount: shouldApplyBatchDiscount ? batchDiscount : 0,
        price: finalPrice,
        subtotal: qtyFromBatch * finalPrice,
        hasDiscount: shouldApplyBatchDiscount
      });
      
      remainingQty -= qtyFromBatch;
    }

    if (remainingQty > 0) {
      // Not enough stock
      return null;
    }

    return {
      totalPrice: totalCost,
      averagePrice: totalCost / quantity,
      batchBreakdown: breakdown,
      singleBatch: breakdown.length === 1,
      hasBatchDiscounts: breakdown.some(b => b.hasDiscount)
    };
  };

  const addItem = (inventoryItem) => {
    // Decant items need size selection first
    if (inventoryItem.trackInML && inventoryItem.decantPrices) {
      setDecantDialog({ open: true, item: inventoryItem, size: 5 });
      return;
    }

    const existing = selectedItems.find(item => item.id === inventoryItem.id);
    
    if (existing) {
      // Increasing quantity
      const newQuantity = existing.quantity + 1;
      
      if (newQuantity > inventoryItem.stock) {
        showSnackbar(`Only ${inventoryItem.stock} units available in stock`, 'warning');
        return;
      }
      
      // Recalculate FIFO pricing for new quantity
      const pricing = calculateFIFOPricing(inventoryItem, newQuantity, formData.customerType);
      
      if (!pricing) {
        showSnackbar('Insufficient stock', 'error');
        return;
      }
      
      setSelectedItems(selectedItems.map(item =>
        item.id === inventoryItem.id
          ? { 
              ...item,
              category: item.category,
              quantity: newQuantity,
              price: pricing.averagePrice,
              totalPrice: pricing.totalPrice,
              batchBreakdown: pricing.batchBreakdown,
              singleBatch: pricing.singleBatch,
              hasBatchDiscounts: pricing.hasBatchDiscounts
            }
          : item
      ));
    } else {
      // Adding new item
      if (inventoryItem.stock === 0) {
        showSnackbar('Item is out of stock', 'error');
        return;
      }
      
      // Calculate FIFO pricing for quantity 1
      const pricing = calculateFIFOPricing(inventoryItem, 1, formData.customerType);
      
      if (!pricing) {
        showSnackbar('Insufficient stock', 'error');
        return;
      }
      
      setSelectedItems([...selectedItems, {
        id: inventoryItem.id,
        name: inventoryItem.name,
        category: inventoryItem.category,
        itemCode: inventoryItem.itemCode || '',
        price: pricing.averagePrice,
        quantity: 1,
        totalPrice: pricing.totalPrice,
        batchBreakdown: pricing.batchBreakdown,
        singleBatch: pricing.singleBatch,
        hasBatchDiscounts: pricing.hasBatchDiscounts
      }]);
    }
    
    // Auto-scroll to selected items section
    setTimeout(() => {
      if (selectedItemsRef.current) {
        selectedItemsRef.current.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest' 
        });
      }
    }, 100);
  };

  // Add a decant item with selected ML size
  const addDecantItem = (inventoryItem, sizeML) => {
    const prices = inventoryItem.decantPrices?.[sizeML] || {};
    // Support both old format (number) and new format (object with retail/wholesale)
    const retailPrice = typeof prices === 'object' ? (prices.retail || 0) : (prices || 0);
    const wholesalePrice = typeof prices === 'object' ? (prices.wholesale || 0) : (prices || 0);
    const price = formData.customerType === 'Wholesale' ? wholesalePrice : retailPrice;
    const decantId = `${inventoryItem.id}_${sizeML}ml`;
    const existing = selectedItems.find(item => item.id === decantId);

    if (inventoryItem.stock < sizeML) {
      showSnackbar(`Not enough ML in stock (${inventoryItem.stock}ml remaining)`, 'warning');
      return;
    }

    if (existing) {
      const newQty = existing.quantity + 1;
      if (newQty * sizeML > inventoryItem.stock) {
        showSnackbar(`Not enough ML in stock (${inventoryItem.stock}ml remaining)`, 'warning');
        return;
      }
      setSelectedItems(selectedItems.map(item =>
        item.id === decantId
          ? { ...item, quantity: newQty, totalPrice: price * newQty }
          : item
      ));
    } else {
      setSelectedItems([...selectedItems, {
        id: decantId,
        inventoryId: inventoryItem.id, // real inventory ID for stock deduction
        name: `${inventoryItem.name} ${sizeML}ml`,
        category: inventoryItem.category,
        itemCode: inventoryItem.itemCode || '',
        price,
        quantity: 1,
        totalPrice: price,
        isDecant: true,
        decantML: sizeML,
        batchBreakdown: [],
        singleBatch: true,
        hasBatchDiscounts: false,
      }]);
    }
    setDecantDialog({ open: false, item: null, size: 5 });
  };

  const updateQuantity = (id, quantity) => {
    const inventoryItem = inventory.find(item => item.id === id);
    
    if (quantity <= 0) {
      setSelectedItems(selectedItems.filter(item => item.id !== id));
      return;
    }
    
    if (inventoryItem && quantity > inventoryItem.stock) {
      showSnackbar(`Only ${inventoryItem.stock} units available in stock`, 'warning');
      return;
    }
    
    // Recalculate FIFO pricing for new quantity
    const pricing = calculateFIFOPricing(inventoryItem, parseInt(quantity), formData.customerType);
    
    if (!pricing) {
      showSnackbar('Insufficient stock', 'error');
      return;
    }
    
    setSelectedItems(selectedItems.map(item =>
      item.id === id 
        ? { 
            ...item,
            category: item.category,
            quantity: parseInt(quantity),
            price: pricing.averagePrice,
            totalPrice: pricing.totalPrice,
            batchBreakdown: pricing.batchBreakdown,
            singleBatch: pricing.singleBatch,
            hasBatchDiscounts: pricing.hasBatchDiscounts
          }
        : item
    ));
  };

  const removeItem = (id) => {
    setSelectedItems(selectedItems.filter(item => item.id !== id));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingBill(null);
    setFormData({ customer: '', date: localDateStr(), status: 'Pending', paymentType: 'Cash', customerType: 'Retail', note: '' });
    setDeliveryCharge(0);
    setSelectedItems([]);
    setDiscount({ type: 'percentage', value: 0 });
    setPaymentSplit({ cash: 0, bank: 0, koko: 0, mintpay: 0 });
    setLoyaltyDiscount({
      eligible: false,
      applied: false,
      amount: 0,
      customerData: null,
      settings: null,
      tier: null
    });
    setInventorySearchTerm(''); // Clear search when closing
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={300} height={50} />
          <Skeleton variant="text" width={400} height={30} />
        </Box>

        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
              <Skeleton variant="text" width={150} height={40} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Skeleton variant="rectangular" width={100} height={36} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" width={150} height={36} sx={{ borderRadius: 1 }} />
              </Box>
            </Box>
            <Skeleton variant="rectangular" width="100%" height={56} sx={{ mb: 3, borderRadius: 1 }} />
            <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 1 }} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Billing Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create and manage invoices for your customers
          </Typography>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              All Invoices
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={refreshing ? <CircularProgress size={20} /> : <RefreshCw size={18} />}
                onClick={handleRefresh}
                disabled={refreshing}
                size="small"
                color="primary"
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Refresh</Box>
              </Button>
              <Button
                variant="contained"
                startIcon={<Plus size={18} />}
                onClick={() => {
                  // Apply global discount from settings when opening new bill
                  const gd = billingSettings?.globalDiscount;
                  if (gd?.enabled && gd?.value > 0) {
                    setDiscount({ type: gd.type, value: gd.value });
                  } else {
                    setDiscount({ type: 'percentage', value: 0 });
                  }
                  setShowModal(true);
                }}
                size="small"
                color="primary"
              >
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Create New Invoice</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
              </Button>
            </Box>
          </Box>

          <TextField
            fullWidth
            placeholder="Search by customer or invoice ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon size={20} />
                </InputAdornment>
              ),
            }}
          />

          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Invoice ID</strong></TableCell>
                  <TableCell><strong>Customer</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Items</strong></TableCell>
                  <TableCell align="right"><strong>Total (LKR)</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedBills.map((bill) => (
                  <TableRow key={bill.id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" color="textSecondary" fontWeight={700}>
                        {bill.invoiceNumber || bill.id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{bill.customer}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={bill.customerType || 'Retail'}
                        size="small"
                        color={bill.customerType === 'Wholesale' ? 'info' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{bill.items ? bill.items.length : 0} item(s)</TableCell>
                    <TableCell align="right" sx={{ py: 2 }}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {(bill.total || bill.amount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                        {bill.paymentType === 'Koko' && bill.kokoCharge && (
                          <Typography variant="caption" color="warning.main" display="block">
                            (incl. {bill.kokoCharge.percentage}% Koko charge)
                          </Typography>
                        )}
                        {bill.paymentType === 'MintPay' && bill.mintPayCharge && (
                          <Typography variant="caption" color="warning.main" display="block">
                            (incl. {bill.mintPayCharge.percentage}% MintPay charge)
                          </Typography>
                        )}
                        {bill.paymentType === 'COD' && bill.deliveryCharge > 0 && (
                          <Typography variant="caption" color="info.main" display="block">
                            +LKR {(bill.deliveryCharge).toLocaleString('en-LK', { minimumFractionDigits: 2 })} delivery
                          </Typography>
                        )}
                        {bill.paymentSplit && (
                          <Box sx={{ mt: 1 }}>
                            <PaymentSplitBadge 
                              paymentSplit={bill.paymentSplit} 
                              billingSettings={billingSettings}
                              variant="outlined"
                            />
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>{bill.date}</TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={bill.status}
                        size="small"
                        color={
                          bill.status === 'Paid' ? 'success' :
                          bill.status === 'Koko' || bill.status === 'MintPay' ? 'warning' :
                          bill.status === 'Pending' ? 'warning' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 2 }}>
                      <Tooltip title={bill.returnStatus ? "Cannot edit bills with returns" : "Edit invoice"}>
                        <span>
                          <IconButton 
                            size="small" 
                            onClick={() => handleEdit(bill)} 
                            disabled={actionLoading[`delete-${bill.id}`] || !!bill.returnStatus}
                          >
                            <Eye size={18} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton size="small" onClick={() => handlePrint(bill)} disabled={actionLoading[`delete-${bill.id}`]}>
                        <Printer size={18} />
                      </IconButton>
                      {canDelete && (
                        <Tooltip title={bill.returnStatus ? "Cannot delete returned bills" : "Delete invoice"}>
                          <span>
                            <IconButton 
                              size="small" 
                              onClick={() => setDeleteConfirm(bill)}
                              disabled={actionLoading[`delete-${bill.id}`] || !!bill.returnStatus}
                            >
                              {actionLoading[`delete-${bill.id}`] ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Trash2 size={18} />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {filteredBills.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No invoices found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={filteredBills.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
          />
        </CardContent>
      </Card>

      <Dialog 
        open={showModal} 
        onClose={closeModal} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: 'calc(100vh - 64px)',
            m: 2,
            height: 'calc(100vh - 64px)',
          }
        }}
      >
        <DialogTitle>{editingBill ? 'Edit Invoice' : 'Create New Invoice'}</DialogTitle>
        <DialogContent
          sx={{
            overflow: 'hidden',
            p: 0,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            height: '100%',
          }}
        >
          {/* Left Side - Form and Items Selection */}
          <Box
            sx={{
              flex: { xs: '1', md: '0 0 60%' },
              p: 3,
              overflow: 'auto',
              borderRight: { md: '1px solid' },
              borderColor: { md: 'divider' },
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
              },
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={10}>
                <Autocomplete
                  freeSolo
                  options={customers}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    return option.name || '';
                  }}
                  filterOptions={(options, state) => {
                    const inputValue = state.inputValue.toLowerCase();
                    return options.filter(option =>
                      option.name.toLowerCase().includes(inputValue) ||
                      (option.phone && option.phone.toLowerCase().includes(inputValue))
                    );
                  }}
                  value={formData.customer ? customers.find(c => c.name === formData.customer) || formData.customer : null}
                  onChange={(event, newValue) => {
                    let customerName = '';
                    if (typeof newValue === 'string') {
                      customerName = newValue;
                      setFormData({ ...formData, customer: newValue });
                    } else if (newValue && newValue.name) {
                      customerName = newValue.name;
                      setFormData({ ...formData, customer: newValue.name });
                    } else {
                      setFormData({ ...formData, customer: '' });
                    }
                    
                    // Check loyalty eligibility when customer is selected
                    if (customerName) {
                      checkCustomerLoyalty(customerName);
                    } else {
                      setLoyaltyDiscount({
                        eligible: false,
                        applied: false,
                        amount: 0,
                        customerData: null,
                        settings: null,
                        tier: null
                      });
                    }
                  }}
                  onInputChange={(event, newInputValue) => {
                    setFormData({ ...formData, customer: newInputValue });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      placeholder="Search customer by name or phone..."
                      required
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.phone || 'No phone'}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="outlined"
                  onClick={() => setShowQuickAddCustomer(true)}
                  fullWidth
                  sx={{ 
                    height: '56px',
                    borderColor: 'text.primary',
                    color: 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <Plus size={20} />
                </Button>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Customer Type</InputLabel>
                  <Select
                    value={formData.customerType}
                    label="Customer Type"
                    onChange={(e) => {
                      setFormData({ ...formData, customerType: e.target.value });
                      setSelectedItems([]);
                    }}
                  >
                    <MenuItem value="Retail">Retail</MenuItem>
                    <MenuItem value="Wholesale">Wholesale</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Payment Type</InputLabel>
                  <Select
                    value={formData.paymentType}
                    label="Payment Type"
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData({ ...formData, paymentType: newType });
                      // Auto-fill delivery charge for COD, reset for others
                      if (newType === 'COD') {
                        setDeliveryCharge(billingSettings?.defaultDeliveryCharge ?? 450);
                      } else {
                        setDeliveryCharge(0);
                      }
                    }}
                  >
                    <MenuItem value="Cash">Cash</MenuItem>
                    <MenuItem value="Card">Card</MenuItem>
                    <MenuItem value="Credit">Credit</MenuItem>
                    <MenuItem value="COD">COD (Cash on Delivery)</MenuItem>
                    <MenuItem value="Koko">Koko</MenuItem>
                    <MenuItem value="MintPay">MintPay</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={12}>
                <Button
                  variant="outlined"
                  onClick={() => setPaymentSplitDialog(true)}
                  fullWidth
                  sx={{
                    py: 1.5,
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText'
                    }
                  }}
                >
                  Split Payment (Optional)
                </Button>
              </Grid>
            </Grid>

            {/* COD Delivery Charge Field */}
            {formData.paymentType === 'COD' && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'info.main', borderRadius: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Delivery Charge (Optional)
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  label="Delivery Charge (LKR)"
                  value={deliveryCharge}
                  onChange={(e) => setDeliveryCharge(parseFloat(e.target.value) || 0)}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ width: 200 }}
                  helperText="Set to 0 if no delivery charge"
                  InputProps={{ startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>LKR</Typography> }}
                />
              </Box>
            )}

            {/* Loyalty Discount Alert */}
            {loyaltyDiscount.eligible && !loyaltyDiscount.applied && (
              <Alert 
                severity="success" 
                sx={{ mt: 2 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={applyLoyaltyDiscount}
                    disabled={selectedItems.length === 0}
                  >
                    Apply Discount
                  </Button>
                }
              >
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {loyaltyDiscount.tier.icon} {loyaltyDiscount.customerData.name} is a {loyaltyDiscount.tier.tier} Customer!
                  </Typography>
                  <Typography variant="caption">
                    {loyaltyDiscount.customerData.totalOrders} previous orders • 
                    LKR {loyaltyDiscount.customerData.totalSpent.toLocaleString()} total spent
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    Loyalty Discount: {loyaltyDiscount.settings.discountValue}
                    {loyaltyDiscount.tier.bonusDiscount > 0 && ` + ${loyaltyDiscount.tier.bonusDiscount}`}
                    {loyaltyDiscount.settings.discountType === 'percentage' ? '%' : ' LKR'} off
                    {loyaltyDiscount.tier.bonusDiscount > 0 && ` (${loyaltyDiscount.tier.tier} bonus)`}
                  </Typography>
                </Box>
              </Alert>
            )}

            {loyaltyDiscount.applied && (
              <Alert 
                severity="info" 
                sx={{ mt: 2 }}
                action={
                  <Button 
                    color="inherit" 
                    size="small" 
                    onClick={removeLoyaltyDiscount}
                  >
                    Remove
                  </Button>
                }
              >
                <Typography variant="body2" fontWeight={600}>
                  {loyaltyDiscount.tier.icon} Loyalty Discount Applied!
                </Typography>
                <Typography variant="caption">
                  {loyaltyDiscount.settings.message || 'Thank you for being a valued customer!'}
                </Typography>
              </Alert>
            )}

            {checkingLoyalty && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Checking loyalty status...</Typography>
                </Box>
              </Alert>
            )}

            {(formData.paymentType === 'Credit' || formData.paymentType === 'COD') && (
              <Alert severity="error" sx={{ mt: 2 }}>
                This customer will be registered as a creditor. You can manage payments in the Creditors module.
              </Alert>
            )}

            {formData.paymentType === 'Koko' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Koko payment: Customer will be registered as a Koko creditor. Payment due in 15 days. You can manage payments in the Creditors module.
              </Alert>
            )}

            {formData.paymentType === 'MintPay' && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                MintPay payment: Customer will be registered as a MintPay creditor. Payment due in 15 days. You can manage payments in the Creditors module.
              </Alert>
            )}

            {formData.customerType === 'Wholesale' && (
              <Alert severity="error" sx={{ mt: 2 }}>
                Wholesale pricing will be applied to all items.
              </Alert>
            )}

            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 3, mb: 2 }}>
              Add Items from Inventory
            </Typography>
            
            {/* Search Bar for Inventory */}
            <TextField
              fullWidth
              placeholder="Search items by name, category, or invoice number..."
              value={inventorySearchTerm}
              onChange={(e) => setInventorySearchTerm(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon size={20} />
                  </InputAdornment>
                ),
              }}
            />
            
            <Grid container spacing={1} sx={{ maxHeight: 300, overflowY: 'auto', mb: 3 }}>
              {(() => {
                const searchLower = inventorySearchTerm.toLowerCase();
                const filtered = inventory.filter(item =>
                  !item.isInternal && (
                  item.name.toLowerCase().includes(searchLower) ||
                  (item.category && item.category.toLowerCase().includes(searchLower)) ||
                  (item.itemCode && item.itemCode.toLowerCase().includes(searchLower)) ||
                  (item.invoiceNumber && item.invoiceNumber.toLowerCase().includes(searchLower))
                  )
                );

                if (filtered.length === 0) {
                  return (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No items found matching "{inventorySearchTerm}"
                        </Typography>
                      </Box>
                    </Grid>
                  );
                }

                // Group by category
                const grouped = filtered.reduce((acc, item) => {
                  const cat = item.category || 'Uncategorized';
                  if (!acc[cat]) acc[cat] = [];
                  acc[cat].push(item);
                  return acc;
                }, {});

                return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => (
                  <React.Fragment key={category}>
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        fontWeight={700}
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 1, px: 0.5 }}
                      >
                        {category}
                      </Typography>
                      <Divider sx={{ mt: 0.5, mb: 1 }} />
                    </Grid>
                    {items.map(item => {
                      const activeBatches = item.batches
                        ? item.batches.filter(b => (b.quantityRemaining || 0) > 0)
                        : [];
                      const oldestBatch = activeBatches.length > 0
                        ? [...activeBatches].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))[0]
                        : null;
                      const retailPrice = oldestBatch?.retailPrice || item.retailPrice || item.sellingPrice || 0;
                      const wholesalePrice = oldestBatch?.wholesalePrice || item.wholesalePrice || item.sellingPrice || item.retailPrice || 0;
                      const displayPrice = formData.customerType === 'Wholesale' ? wholesalePrice : retailPrice;

                      return (
                        <Grid item xs={6} sm={4} md={3} key={item.id}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => addItem(item)}
                            disabled={item.stock === 0}
                            sx={{
                              py: 2.5,
                              px: 1.5,
                              textAlign: 'left',
                              justifyContent: 'flex-start',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              height: '100%',
                              minHeight: '140px',
                              borderColor: 'text.primary',
                              color: 'text.primary',
                              '&:hover': {
                                borderColor: 'primary.main',
                                backgroundColor: 'action.hover'
                              }
                            }}
                          >
                            <Typography variant="body2" fontWeight={600} sx={{ width: '100%', wordBreak: 'break-word', mb: 0.5 }}>
                              {item.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.5 }}>
                              {item.category}
                            </Typography>
                            {item.itemCode && (
                              <Typography variant="caption" color="primary.main" sx={{ fontSize: '0.65rem', mb: 0.5 }}>
                                {item.itemCode}
                              </Typography>
                            )}
                            <Typography variant="caption" color="success.main" fontWeight={600} sx={{ mb: 0.5 }}>
                              {formData.customerType === 'Wholesale' ? 'Wholesale: ' : 'Retail: '}
                              {displayPrice.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </Typography>
                            {retailPrice > 0 && wholesalePrice > 0 && retailPrice !== wholesalePrice && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', mb: 0.5 }}>
                                {formData.customerType === 'Wholesale'
                                  ? `Retail: ${retailPrice.toFixed(2)}`
                                  : `Wholesale: ${wholesalePrice.toFixed(2)}`
                                }
                              </Typography>
                            )}
                            <Typography variant="caption" color={item.stock > 10 ? 'text.secondary' : 'error.main'} sx={{ mt: 'auto' }}>
                              Stock: {item.stock}
                            </Typography>
                          </Button>
                        </Grid>
                      );
                    })}
                  </React.Fragment>
                ));
              })()}
            </Grid>
          </Box>

          {/* Right Side - Order Summary */}
          <Box
            sx={{
              flex: { xs: '1', md: '0 0 40%' },
              p: 3,
              overflow: 'auto',
              backgroundColor: 'action.hover',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
              },
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
              Order Summary
            </Typography>

            {selectedItems.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Package size={48} style={{ opacity: 0.3, marginBottom: 16 }} />
                <Typography color="text.secondary">
                  No items selected
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Add items from the left to create an invoice
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ minHeight: 200, maxHeight: 320, overflow: 'auto', mb: 2 }}>
                  {selectedItems.map(item => (
                    <Card key={item.id} sx={{ mb: 1.5, backgroundColor: 'background.paper' }}>
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
                          <Typography variant="body2" fontWeight={600} sx={{ flex: 1 }}>
                            {item.name} ({item.category})
                          </Typography>
                          <IconButton size="small" onClick={() => removeItem(item.id)} sx={{ ml: 1 }}>
                            <Trash2 size={16} />
                          </IconButton>
                        </Box>
                        
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              type="number"
                              size="small"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.id, e.target.value)}
                              sx={{ width: 70 }}
                              inputProps={{ min: 1 }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              × LKR {item.price.toFixed(2)} {!item.singleBatch ? 'avg' : ''}
                            </Typography>
                          </Box>
                          <Typography variant="body2" fontWeight={700}>
                            LKR {(item.totalPrice || (item.price * item.quantity)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>

                {/* Bottom section — discount, totals, note */}
                <Box sx={{ flexShrink: 0 }}>
                <Divider sx={{ my: 2 }} />

                {/* Discount Section */}
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                  Discount (Optional)
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  <Grid item xs={5}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={discount.type}
                        onChange={(e) => setDiscount({ ...discount, type: e.target.value })}
                      >
                        <MenuItem value="percentage">%</MenuItem>
                        <MenuItem value="fixed">LKR</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={7}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={discount.value}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        const max = discount.type === 'percentage' ? 100 : calculateSubtotal();
                        setDiscount({ ...discount, value: Math.max(0, Math.min(val, max)) });
                      }}
                      placeholder={discount.type === 'percentage' ? 'Percentage' : 'Amount'}
                      inputProps={{ 
                        min: 0, 
                        max: discount.type === 'percentage' ? 100 : calculateSubtotal(),
                        step: discount.type === 'percentage' ? 1 : 0.01
                      }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Totals */}
                <Box sx={{ backgroundColor: 'background.paper', p: 2, borderRadius: 2 }}>
                  {/* Show batch discount breakdown if any items have batch discounts */}
                  {(() => {
                    const globalDiscountEnabled = billingSettings?.globalDiscount?.enabled || false;
                    const hasBatchDiscounts = !globalDiscountEnabled && selectedItems.some(item => item.hasBatchDiscounts);
                    
                    if (hasBatchDiscounts) {
                      // Calculate original subtotal (without batch discounts)
                      let originalSubtotal = 0;
                      let totalBatchDiscount = 0;
                      
                      selectedItems.forEach(item => {
                        if (item.batchBreakdown && item.hasBatchDiscounts) {
                          item.batchBreakdown.forEach(batch => {
                            if (batch.hasDiscount) {
                              originalSubtotal += batch.originalPrice * batch.quantity;
                              totalBatchDiscount += (batch.originalPrice - batch.price) * batch.quantity;
                            } else {
                              originalSubtotal += batch.price * batch.quantity;
                            }
                          });
                        } else {
                          originalSubtotal += item.totalPrice || (item.price * item.quantity);
                        }
                      });
                      
                      const discountedSubtotal = calculateSubtotal();
                      
                      return (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Original Subtotal:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              LKR {originalSubtotal.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                              Batch Discount Applied:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>
                              - LKR {totalBatchDiscount.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2">Subtotal (After Batch Discount):</Typography>
                            <Typography variant="body2" fontWeight={600} color="success.main">
                              LKR {discountedSubtotal.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                            </Typography>
                          </Box>
                        </>
                      );
                    } else {
                      return (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">Subtotal:</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            LKR {calculateSubtotal().toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                          </Typography>
                        </Box>
                      );
                    }
                  })()}
                  
                  {discount.value > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Additional Discount ({discount.type === 'percentage' ? `${discount.value}%` : 'Fixed'}):
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        - LKR {calculateDiscount().toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  )}

                  {loyaltyDiscount.applied && loyaltyDiscount.amount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Loyalty Discount ({loyaltyDiscount.tier?.tier}):
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        - LKR {(loyaltyDiscount.amount || 0).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                  )}
                  
                  {formData.paymentType === 'Koko' && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Subtotal (Before Koko Charge):
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          LKR {(calculateSubtotal() - calculateDiscount() - (loyaltyDiscount.applied ? loyaltyDiscount.amount || 0 : 0)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          Koko Service Charge ({billingSettings?.kokoChargePercentage || 12}%):
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          + LKR {calculateKokoCharge().toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {formData.paymentType === 'MintPay' && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Subtotal (Before MintPay Charge):
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          LKR {(calculateSubtotal() - calculateDiscount() - (loyaltyDiscount.applied ? loyaltyDiscount.amount || 0 : 0)).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          MintPay Service Charge ({billingSettings?.mintPayChargePercentage || 12}%):
                        </Typography>
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          + LKR {calculateMintPayCharge().toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </>
                  )}

                  {formData.paymentType === 'COD' && parseFloat(deliveryCharge) > 0 && (
                    <>
                      <Divider sx={{ my: 1.5 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" color="info.main" fontWeight={600}>
                          Delivery Charge:
                        </Typography>
                        <Typography variant="body2" color="info.main" fontWeight={600}>
                          + LKR {parseFloat(deliveryCharge).toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </>
                  )}
                  
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight={700}>Total:</Typography>
                    <Typography variant="h6" fontWeight={700} color="text.primary">
                      LKR {calculateTotal().toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                    </Typography>
                  </Box>
                </Box>

                {/* Note */}
                <TextField
                  fullWidth
                  label="Note (optional)"
                  multiline
                  rows={2}
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="e.g. Handle with care, delivery instructions..."
                  sx={{ mt: 2 }}
                />
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button 
            onClick={closeModal} 
            disabled={submitting}
            sx={{
              borderColor: 'text.primary',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCreate}
            disabled={submitting || selectedItems.length === 0}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              backgroundColor: 'primary.main',
              color: 'common.white',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            {submitting ? (editingBill ? 'Updating...' : 'Creating...') : (editingBill ? 'Update Invoice' : 'Create Invoice')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Decant Size Selector Dialog */}
      <Dialog open={decantDialog.open} onClose={() => setDecantDialog({ open: false, item: null, size: 5 })} maxWidth="xs" fullWidth>
        <DialogTitle>Select Decant Size</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {decantDialog.item?.name} — {decantDialog.item?.stock}ml remaining
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {[5, 10].map((ml) => {
              const prices = decantDialog.item?.decantPrices?.[ml] || {};
              const retail = typeof prices === 'object' ? (prices.retail || 0) : (prices || 0);
              const wholesale = typeof prices === 'object' ? (prices.wholesale || 0) : (prices || 0);
              const displayPrice = formData.customerType === 'Wholesale' ? wholesale : retail;
              const available = Math.floor((decantDialog.item?.stock || 0) / ml);
              return (
                <Button
                  key={ml}
                  fullWidth
                  variant={decantDialog.size === ml ? 'contained' : 'outlined'}
                  onClick={() => setDecantDialog((d) => ({ ...d, size: ml }))}
                  disabled={available === 0}
                  sx={{ py: 2, flexDirection: 'column' }}
                >
                  <Typography variant="h6" fontWeight={700}>{ml}ml</Typography>
                  <Typography variant="caption">LKR {displayPrice.toLocaleString('en-LK', { minimumFractionDigits: 2 })}</Typography>
                  {retail !== wholesale && (
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
                      {formData.customerType === 'Wholesale' ? `Retail: ${retail.toFixed(2)}` : `Wholesale: ${wholesale.toFixed(2)}`}
                    </Typography>
                  )}
                  <Typography variant="caption" color={available > 0 ? 'success.main' : 'error.main'}>
                    {available} available
                  </Typography>
                </Button>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDecantDialog({ open: false, item: null, size: 5 })}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => addDecantItem(decantDialog.item, decantDialog.size)}
            disabled={!decantDialog.item?.decantPrices?.[decantDialog.size]}
          >
            Add {decantDialog.size}ml
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Invoice?</DialogTitle>
        <DialogContent>
          {deleteConfirm?.returnStatus ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              This invoice has returns associated with it. Please delete the return records first from the Returns page.
            </Alert>
          ) : (
            <Typography>
              Delete invoice for <strong>{deleteConfirm?.customer}</strong>? Stock will be returned to inventory. This cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} disabled={!!actionLoading[`delete-${deleteConfirm?.id}`]}>Cancel</Button>
          <Button variant="contained" color="error"
            disabled={!!actionLoading[`delete-${deleteConfirm?.id}`] || !!deleteConfirm?.returnStatus}
            startIcon={actionLoading[`delete-${deleteConfirm?.id}`] ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={() => { handleDelete(deleteConfirm?.id); setDeleteConfirm(null); }}>
            {actionLoading[`delete-${deleteConfirm?.id}`] ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Quick Add Customer Dialog */}
      <Dialog 
        open={showQuickAddCustomer} 
        onClose={() => setShowQuickAddCustomer(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: 'calc(100vh - 64px)',
            m: 2,
          }
        }}
      >
        <DialogTitle>Quick Add Customer</DialogTitle>
        <DialogContent
          sx={{
            overflow: 'auto',
          }}
        >
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Customer Name"
              required
              value={quickCustomerData.name}
              onChange={(e) => setQuickCustomerData({ ...quickCustomerData, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={quickCustomerData.phone}
              onChange={(e) => setQuickCustomerData({ ...quickCustomerData, phone: e.target.value })}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Email (Optional)"
              type="email"
              value={quickCustomerData.email}
              onChange={(e) => setQuickCustomerData({ ...quickCustomerData, email: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowQuickAddCustomer(false)}
            sx={{
              borderColor: 'text.primary',
              color: 'text.primary',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleQuickAddCustomer}
            sx={{
              backgroundColor: 'primary.main',
              color: 'common.white',
              '&:hover': {
                backgroundColor: 'primary.dark'
              }
            }}
          >
            Add Customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Split Dialog */}
      <PaymentSplitDialog
        open={paymentSplitDialog}
        onClose={() => setPaymentSplitDialog(false)}
        onConfirm={(split) => {
          setPaymentSplit(split);
          showSnackbar('Payment split configured');
        }}
        totalAmount={calculateTotal()}
        billingSettings={billingSettings}
        currentPaymentType={formData.paymentType}
      />
    </Box>
  );
};

export default Billing;
